/* eslint-disable react/no-unknown-property */
import { utcToLocalDate } from 'common/utils/date';
import IconBox from 'components/commons/icon-box';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import Button from 'components/elements/button';
import { format } from 'date-fns';
import { id, enUS } from 'date-fns/locale';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { ArrowRight, Edit } from 'react-feather';
import color from 'styles/color';

import {
  Dot,
  MobilePortText,
  MobileTextContent,
  SectionContent,
  ShippingRouteContainer,
  SmallLabel,
  TextContent,
} from './style';

interface Props {
  from: string;
  to: string;
  departureTime: string;
  passenger?: number;
  vehicleType?: number;
  openParent: (boolean) => void;
  parentOpen: boolean;
}

export default function SelectShippingRoute(props: Props) {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const onOpenDialog = React.useCallback(() => {
    props.openParent(!props.parentOpen);
  }, [props]);

  const departureLabel = React.useMemo(() => {
    return format(utcToLocalDate(props.departureTime), 'dd MMMM yyyy', {
      locale: locale === 'en' ? enUS : id,
    });
  }, [locale, props.departureTime]);

  return (
    <>
      <style jsx global>{`
        body {
          overflow-y: ${props.parentOpen ? 'hidden' : 'scroll'};
        }
      `}</style>

      <ShippingRouteContainer>
        <SectionContent css={{ py: 16 }}>
          <Stack
            gap="md"
            rootCss={{
              display: 'none',
              '@lg': {
                display: 'flex',
              },
            }}
          >
            <Stack.Item>
              <Text variant="headline5" weight="bold">
                {t('common:select_cruise_route')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <Stack direction="horizontal" justify="between">
                <Stack.Item grow>
                  <Stack direction="horizontal" gap="md" alignItems="center">
                    <Stack.Item>
                      <Stack
                        direction="horizontal"
                        gap="sm"
                        alignItems="center"
                      >
                        <Stack.Item>
                          <SmallLabel>{t('common:from')}</SmallLabel>
                          <TextContent variant="body4">
                            {props.from}
                          </TextContent>
                        </Stack.Item>
                        <ArrowRight size={16} color={color.primary} />
                        <Stack.Item>
                          <SmallLabel>{t('common:to')}</SmallLabel>
                          <TextContent variant="body4">{props.to}</TextContent>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                    <Dot />
                    <Stack.Item>
                      <SmallLabel>{t('common:departure_time')}</SmallLabel>
                      <TextContent variant="body4">
                        {departureLabel}
                      </TextContent>
                    </Stack.Item>
                    <Stack.Item direction="horizontal">
                      {!!props.passenger && (
                        <Stack.Item css={{ mr: 16 }}>
                          <Stack
                            direction="horizontal"
                            alignItems="center"
                            gap="md"
                          >
                            <Dot />
                            <Stack.Item>
                              <SmallLabel>
                                {t('common:passenger_count')}
                              </SmallLabel>
                              <TextContent variant="body4">
                                {t('common:count_passenger', {
                                  count: props.passenger,
                                })}
                              </TextContent>
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      )}
                      {!!props.vehicleType && (
                        <Stack
                          direction="horizontal"
                          alignItems="center"
                          gap="md"
                        >
                          <Dot />
                          <Stack.Item>
                            <SmallLabel>{t('common:vehicle_type')}</SmallLabel>
                            <TextContent variant="body4">
                              {t('common:count_vehicle', {
                                count: props.vehicleType,
                              })}
                            </TextContent>
                          </Stack.Item>
                        </Stack>
                      )}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
                <Stack.Item css={{ ml: 16 }}>
                  <Button
                    variant="secondary"
                    onClick={onOpenDialog}
                    css={{ minWidth: 152 }}
                  >
                    {t('common:change_route')}
                  </Button>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
          <Stack
            rootCss={{
              display: 'flex',
              '@lg': {
                display: 'none',
              },
            }}
          >
            <Stack.Item css={{ mb: 8 }}>
              <Stack gap="md" direction="horizontal" alignItems="center">
                <MobilePortText variant="headline7">
                  {props.from}
                </MobilePortText>
                <ArrowRight color={color.neutral8} />
                <MobilePortText variant="headline7">{props.to}</MobilePortText>
              </Stack>
            </Stack.Item>
            <Stack.Item direction="horizontal" justify="between">
              <Stack gap="sm" direction="horizontal" alignItems="center">
                <Stack.Item>
                  <MobileTextContent variant="body5">
                    {departureLabel}
                  </MobileTextContent>
                </Stack.Item>
                {!!props.passenger && (
                  <Stack direction="horizontal" gap="sm" alignItems="center">
                    <Dot css={{ backgroundColor: '$typeGray' }} />
                    <Stack.Item>
                      <MobileTextContent variant="body5">
                        {t('common:count_passenger', {
                          count: props.passenger,
                        })}
                      </MobileTextContent>
                    </Stack.Item>
                  </Stack>
                )}
                {!!props.vehicleType && (
                  <Stack direction="horizontal" gap="sm" alignItems="center">
                    <Dot css={{ backgroundColor: '$typeGray' }} />
                    <Stack.Item>
                      <MobileTextContent variant="body5">
                        {t('common:count_vehicle', {
                          count: props.vehicleType,
                        })}
                      </MobileTextContent>
                    </Stack.Item>
                  </Stack>
                )}
              </Stack>
              <IconBox onClick={onOpenDialog}>
                <Edit color={color.primary} />
              </IconBox>
            </Stack.Item>
          </Stack>
        </SectionContent>
      </ShippingRouteContainer>
    </>
  );
}
